import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { ArrowLeft, Download, HardDrive, Library, Search, Trash2, Video } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatBytes(value) {
  if (value == null) return "Unlimited";
  const bytes = Number(value || 0);
  if (bytes < 1_000_000_000) return `${(bytes / 1_000_000).toFixed(bytes ? 1 : 0)} MB`;
  return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
}

export default function CommunicationsRecordings() {
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState([]);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [sort, setSort] = useState("newest");

  const load = async () => {
    setLoading(true);
    try {
      const [recordingsResponse, storageResponse] = await Promise.all([
        axios.get(`${API}/communications/recordings`),
        axios.get(`${API}/communications/storage`),
      ]);
      setRecordings(recordingsResponse.data || []);
      setStorage(storageResponse.data || null);
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) navigate("/yasharal-live/login");
      else toast.error(error?.response?.data?.detail || "Recordings could not be loaded");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visibleRecordings = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recordings
      .filter((recording) => {
        if (visibility !== "all" && String(recording.visibility || "internal") !== visibility) return false;
        if (!q) return true;
        return [recording.title, recording.description, recording.created_by_display_name, recording.created_by_username]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        if (sort === "oldest") return new Date(a.recorded_at || 0) - new Date(b.recorded_at || 0);
        if (sort === "title") return String(a.title || "").localeCompare(String(b.title || ""));
        return new Date(b.recorded_at || 0) - new Date(a.recorded_at || 0);
      });
  }, [recordings, search, visibility, sort]);

  const remove = async (recording) => {
    if (!window.confirm(`Delete “${recording.title}” permanently? This frees its storage space.`)) return;
    try {
      await axios.delete(`${API}/communications/recordings/${recording.id}`);
      toast.success("Recording deleted and storage released");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Recording could not be deleted");
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0d11] text-white py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Link to="/yasharal-live" className="inline-flex items-center gap-2 text-[#9dc1ff] font-semibold mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Yasharal Live Studio
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Library className="w-8 h-8 text-kingdom-gold" />
          <div>
            <h1 className="text-3xl font-semibold">Yasharal Live Recordings</h1>
            <p className="text-slate-400 mt-1">Recordings this account is authorized to view.</p>
          </div>
        </div>

        {storage?.plan && (
          <div className="rounded-2xl border border-slate-800 bg-[#111318] p-5 mb-8">
            <div className="flex items-center gap-2 font-semibold"><HardDrive className="w-5 h-5 text-[#9dc1ff]" /> {storage.plan.name}</div>
            <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
              <div><div className="text-slate-500">Storage used</div><div className="font-semibold mt-1">{formatBytes(storage.storage_bytes)}</div></div>
              <div><div className="text-slate-500">Storage limit</div><div className="font-semibold mt-1">{storage.storage_limit_bytes == null ? "Unlimited" : formatBytes(storage.storage_limit_bytes)}</div></div>
              <div><div className="text-slate-500">Recordings</div><div className="font-semibold mt-1">{storage.recording_count}{storage.recording_count_limit == null ? "" : ` / ${storage.recording_count_limit}`}</div></div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_auto_auto] gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search recording title, description, or host…" className="pl-9 bg-[#111318] border-slate-700 text-white" />
          </div>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="rounded-md bg-[#111318] border border-slate-700 px-3 py-2 text-sm">
            <option value="all">All access levels</option>
            <option value="internal">Internal</option>
            <option value="public">Public</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-md bg-[#111318] border border-slate-700 px-3 py-2 text-sm">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="title">Title A–Z</option>
          </select>
        </div>

        {loading ? (
          <p className="text-slate-500">Loading recordings…</p>
        ) : visibleRecordings.length === 0 ? (
          <div className="rounded-2xl border border-slate-800 bg-[#111318] py-14 text-center text-slate-500">No recordings match this view.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {visibleRecordings.map((recording) => (
              <article key={recording.id} className="overflow-hidden rounded-2xl border border-slate-800 bg-[#111318]">
                <div className="aspect-video bg-slate-950">
                  {recording.thumbnail_url ? (
                    <img src={recording.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Video className="w-12 h-12 text-white/20" /></div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-semibold">{recording.title}</h2>
                    <span className="text-[10px] uppercase px-2 py-1 rounded bg-[#183c73] text-[#9dc1ff]">{recording.visibility}</span>
                  </div>
                  {recording.recorded_at && <p className="text-xs text-slate-500 mt-2">{new Date(recording.recorded_at).toLocaleString()}</p>}
                  {recording.size_bytes != null && <p className="text-xs text-slate-500 mt-1">{formatBytes(recording.size_bytes)}</p>}
                  {recording.description && <p className="text-sm text-slate-400 mt-3 line-clamp-2">{recording.description}</p>}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <a href={recording.file_url} target="_blank" rel="noreferrer"><Button size="sm" className="bg-[#8bb6ff] text-slate-950">Watch</Button></a>
                    {recording.download_url && <a href={recording.download_url}><Button size="sm" variant="outline" className="border-slate-600 text-white"><Download className="w-4 h-4 mr-1" /> Download</Button></a>}
                    {recording.owned_by_current_user && <Button size="sm" variant="outline" className="border-red-500/40 text-red-300" onClick={() => remove(recording)}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
